import { supabase } from '../lib/supabase';

const stripeService = {
  /**
   * Create a Stripe customer for a company
   */
  async createCustomer(companyId) {
    const { data, error } = await supabase?.functions?.invoke('stripe-checkout', {
      body: { action: 'create-customer', companyId },
    });

    if (error) throw new Error(error.message || 'Error creating Stripe customer');
    return data;
  },

  /**
   * Create a Stripe Checkout Session for plan subscription
   */
  async createCheckoutSession({ companyId, planId, billingCycle }) {
    const appUrl = window.location?.origin;

    const { data, error } = await supabase?.functions?.invoke('stripe-checkout', {
      body: {
        action: 'create-checkout-session',
        companyId,
        planId,
        billingCycle,
        successUrl: `${appUrl}/subscription-success?session_id={CHECKOUT_SESSION_ID}&company_id=${companyId}`,
        cancelUrl: `${appUrl}/company-registration`,
      },
    });

    if (error) throw new Error(error.message || 'Error creating checkout session');
    return data;
  },

  /**
   * Open Stripe Billing Portal for subscription management
   */
  async openBillingPortal(companyId) {
    const appUrl = window.location?.origin;

    const { data, error } = await supabase?.functions?.invoke('stripe-checkout', {
      body: {
        action: 'create-billing-portal',
        companyId,
        successUrl: `${appUrl}/workshop-dashboard`,
      },
    });

    if (error) throw new Error(error.message || 'Error opening billing portal');
    if (data?.url) {
      window.location.href = data?.url;
    }
    return data;
  },

  /**
   * Get subscription status for a company
   */
  async getSubscriptionStatus(companyId) {
    const { data, error } = await supabase?.from('company_subscriptions')?.select(`
        *,
        subscription_plans (*)
      `)?.eq('company_id', companyId)?.single();

    if (error) {
      console.error('Error fetching subscription status:', error?.message);
      return null;
    }
    return data;
  },

  /**
   * Get subscription invoices for a company
   */
  async getSubscriptionInvoices(companyId) {
    const { data, error } = await supabase?.from('subscription_invoices')?.select('*')?.eq('company_id', companyId)?.order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching subscription invoices:', error?.message);
      return [];
    }
    return data || [];
  },

  /**
   * Check if subscription is active (not expired or past_due beyond grace period)
   */
  isSubscriptionActive(subscription) {
    if (!subscription) return false;

    const { status, current_period_end, trial_ends_at } = subscription;

    if (status === 'active') return true;
    if (status === 'trial') {
      const trialEnd = trial_ends_at ? new Date(trial_ends_at) : null;
      return trialEnd ? trialEnd > new Date() : true;
    }
    if (status === 'past_due') {
      // Allow access during grace period (check current_period_end + 3 days)
      const periodEnd = current_period_end ? new Date(current_period_end) : null;
      if (periodEnd) {
        const gracePeriodEnd = new Date(periodEnd);
        gracePeriodEnd?.setDate(gracePeriodEnd?.getDate() + 3);
        return gracePeriodEnd > new Date();
      }
    }
    return false;
  },

  /**
   * Redirect to Stripe Checkout for a paid plan
   */
  async redirectToCheckout({ companyId, planId, billingCycle }) {
    try {
      const { sessionUrl } = await stripeService?.createCheckoutSession({
        companyId,
        planId,
        billingCycle,
      });

      if (sessionUrl) {
        window.location.href = sessionUrl;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (err) {
      console.error('Redirect to checkout error:', err);
      throw err;
    }
  },
};

export default stripeService;
